import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function CompanyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const companySchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'おかやま健康製薬株式会社',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    description: '岡山市の健康サプリメント専門メーカー。全世代の健康をサポートする高品質サプリメントを提供。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北区○○町1-2-3',
      addressLocality: '岡山市',
      addressRegion: '岡山県',
      postalCode: '700-0000',
      addressCountry: 'JP',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '34.6617',
      longitude: '133.9350',
    },
    telephone: '+81-86-012-3456',
    faxNumber: '+81-86-012-3457',
    email: 'info@okayama-health.co.jp',
    foundingDate: '2000',
    numberOfEmployees: 5,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    areaServed: {
      '@type': 'Country',
      name: 'Japan',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '会社概要 | おかやま健康製薬株式会社',
    description: 'おかやま健康製薬株式会社の会社概要、所在地、アクセス情報。',
    url: `${siteUrl}/company`,
    inLanguage: 'ja-JP',
    isPartOf: {
      '@type': 'WebSite',
      name: 'おかやま健康製薬株式会社',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '会社概要',
          item: `${siteUrl}/company`,
        },
      ],
    },
  };

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [companySchema, webPageSchema],
  };

  const companyInfo = [
    { label: '会社名', value: 'おかやま健康製薬株式会社' },
    { label: '代表取締役', value: '岡山 太郎' },
    { label: '所在地', value: '〒700-0000 岡山県岡山市北区○○町1-2-3' },
    { label: '電話番号', value: '086-012-3456' },
    { label: 'FAX', value: '086-012-3457' },
    { label: 'メールアドレス', value: 'info@okayama-health.co.jp' },
    { label: '設立', value: '2000年' },
    { label: '従業員数', value: '5名' },
    { label: '営業時間', value: '9:00 - 18:00（平日）' },
    { label: '定休日', value: '土曜日・日曜日・祝日' },
    {
      label: '事業内容',
      value: '健康食品・サプリメントの企画・開発・製造・販売',
    },
    {
      label: '資格・認可',
      value: 'ISO 9001認証、GMP認証、HACCP認証、健康食品GMP認証',
    },
    {
      label: '加入団体',
      value: '日本健康食品規格協会、岡山県商工会議所',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="会社概要 | 岡山の健康サプリメント専門メーカー | おかやま健康製薬株式会社"
        description="おかやま健康製薬株式会社の会社概要、所在地、アクセス情報。岡山市北区に本社を構え、2000年の創業以来、全世代の健康をサポートする高品質サプリメントを提供。ISO認証取得、GMP認証取得により、安心・安全な製品をお届けします。"
        keywords="岡山,会社概要,所在地,アクセス,おかやま健康製薬,健康食品メーカー"
        canonical={`${siteUrl}/company`}
        schema={combinedSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[40vh] mt-16 lg:mt-20 bg-gradient-to-br from-[#4A90E2] to-[#2A6D8D] flex items-center justify-center">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/73d6301c9a30ff2c2f42fda8be3d50e6.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">会社概要</h1>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="w-full h-[500px] lg:h-auto">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/66a340aeb60a0cbc7c700b3469b0499f.jpeg"
                alt="おかやま健康製薬株式会社 外観"
                className="w-full h-full object-cover object-top rounded-3xl shadow-xl"
              />
            </div>
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden h-fit">
              <div className="divide-y divide-gray-200">
                {companyInfo.map((info, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 lg:p-5 hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-bold text-[#1A4D6D] text-sm lg:text-base">
                      {info.label}
                    </div>
                    <div className="sm:col-span-2 text-gray-700 text-sm lg:text-base">
                      {info.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Access Map Section */}
      <section className="py-12 lg:py-20 bg-[#F5FAFE]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">アクセス</h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto"></div>
          </div>
          <div className="bg-white rounded-3xl overflow-hidden shadow-xl">
            <div className="w-full h-64 sm:h-80 lg:h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64498051434!2d133.88447!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e30e8d5b8b%3A0x782f36d7a0e19893!2z5bKh5bGx55yM5bKh5bGx5biC!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="会社所在地"
              ></iframe>
            </div>
            <div className="p-6 lg:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-bold text-[#1A4D6D] mb-4 flex items-center">
                    <i className="ri-map-pin-line mr-2 text-[#4A90E2]"></i>
                    所在地
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    〒700-0000
                    <br />
                    岡山県岡山市北区○○町1-2-3
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1A4D6D] mb-4 flex items-center">
                    <i className="ri-train-line mr-2 text-[#4A90E2]"></i>
                    アクセス方法
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    JR岡山駅より徒歩15分
                    <br />
                    岡山電鉄「○○町」電停より徒歩3分
                    <br />
                    駐車場：3台完備
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}